import { Typography, Skeleton } from '@mui/material'
import { APIError } from 'components/core/errors/api'
import { InsightItem } from 'components/core/insights'
import { SearchAppsRequest } from 'pbts/c1/api/app/v1/app'
import { InsightType } from 'pbts/c1/api/task/v1/insights'
import { User, UserAttributeMappingSource } from 'pbts/c1/api/user/v1/user'
import React, { FC } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { appSearchNonExpandedApi } from 'redux_toolkit/apps'

type EmploymentStatusLabelProps = {
    employmentStatus: string
    employmentStatusSources: UserAttributeMappingSource[]
    user?: User
}

export const EmploymentStatusLabel: FC<EmploymentStatusLabelProps> = ({ employmentStatus, employmentStatusSources, user }) => {
    const intl = useIntl()
    if (!employmentStatus || !employmentStatusSources || employmentStatusSources.length === 0) {
        return null
    }
    const appIds = employmentStatusSources.map((source) => source.appId)
    const actionable = appIds && appIds.length > 0
    const {
        data,
        isLoading,
        isError,
        error,
    } = appSearchNonExpandedApi.useSearch(SearchAppsRequest.create({ appIds }), { actionable: !!actionable })

    if (isLoading && actionable) {
        return <Skeleton variant="text" />
    }
    if (isError) {
        return <APIError err={error} />
    }
    return (
        <Typography>
            <FormattedMessage
                id="components_insights_employment_status"
                defaultMessage="{user} has <b>{employment_status}</b> employment status in {directory}"
                values={{
                    b: (chunks) => <b>{chunks}</b>,
                    user: user?.displayName || intl.formatMessage({ id: 'user_status_unknown_user', defaultMessage: 'This user' }),
                    employment_status: employmentStatus.toLowerCase(),
                    directory: data && data.list.length > 0 ? data.list.map((directory) => directory.displayName).join(', ') : intl.formatMessage({ id: 'employment_status_unknown_directory', defaultMessage: 'the directory' }),
                }}
            />
        </Typography>
    )
}

type EmploymentStatusProps = {
    employmentStatus: string
    employmentStatusSources: UserAttributeMappingSource[]
    insightType: InsightType
    user?: User
}

export const EmploymentStatus: FC<EmploymentStatusProps> = ({ employmentStatus, employmentStatusSources, insightType, user }) => {
    if (!employmentStatus || !employmentStatusSources || employmentStatusSources.length === 0) {
        return null
    }
    return (
        <InsightItem key="user-status" insightType={insightType}>
            <EmploymentStatusLabel employmentStatus={employmentStatus} employmentStatusSources={employmentStatusSources} user={user} />
        </InsightItem>
    )
}
